import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSObjectMethods = () => {
    const methodMechanicsCode = `// 1. Regular Function Property
const worker = {
    role: 'Architect',
    greet: function() { return \`Role: \${this.role}\`; }
};

// 2. ES6 Method Shorthand (Idiomatic)
const architect = {
    role: 'Principal',
    greet() { return \`Role: \${this.role}\`; } // Cleaner syntax
};

// 3. Arrow Function "Method" (WARNING)
const fail = {
    role: 'None',
    greet: () => \`Role: \${this.role}\` //  'this' is lexical (window/global)
};`;

    const bindingRulesCode = `// --- Explicit Binding (call, apply, bind) ---
function logContext(header, footer) {
    console.log(\`\${header}: \${this.user} \${footer}\`);
}

const contextA = { user: 'Alpha' };
const contextB = { user: 'Beta' };

// .call(thisArg, arg1, arg2) -&gt; Immediate invocation
logContext.call(contextA, '>>>', '<<<');

// .apply(thisArg, [args]) -&gt; Immediate invocation
logContext.apply(contextB, ['###', '###']);

// .bind(thisArg) -&gt; Returns a new function with context locked
const boundLog = logContext.bind(contextA, 'FIXED', 'FOOTER');
boundLog();`;

    const fluentApiCode = `// --- The Fluent Interface Pattern (Method Chaining) ---
const queryBuilder = {
    table: '',
    filters: [],
    
    from(tbl) {
        this.table = tbl;
        return this; // Crucial for chaining
    },
    
    where(field, operator, val) {
        this.filters.push(\`\${field} \${operator} \${val}\`);
        return this;
    },
    
    build() {
        return \`SELECT * FROM \${this.table} WHERE \${this.filters.join(' AND ')}\`;
    }
};

const sql = queryBuilder
    .from('users')
    .where('active', '=', 'true')
    .where('role', '=', 'admin')
    .build();`;

    const lostContextCode = `// --- The "Shadow of This" (Lost Context) ---
const service = {
    token: 'XYZ-123',
    fetchData() {
        console.log('Fetching with:', this.token);
    }
};

//  Error: Reference is detached from object
const detached = service.fetchData;
detached(); // 'this' is undefined (strict mode) or global

// Solution: Explicitly bind or use arrow callback
setTimeout(service.fetchData.bind(service), 100);
setTimeout(() => service.fetchData(), 100);`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Object Internals</div>
                <h1>Methods & The Execution Context</h1>
                <p className="hero-lead">
                    In JavaScript, the <code>this</code> keyword is not a static
                    reference to the object containing the function. It is a
                    dynamic variable set at <strong>call-time</strong>, allowing
                    functions to be reused across different data contexts.
                </p>
            </div>

            <div className="content-section">
                <h2>Defining Behavior</h2>
                <p>
                    Methods are properties that hold function references. While
                    regular functions can be assigned as properties, ES6 introduced
                    <strong>method shorthand</strong>, which is the standard way to
                    define behavior in modern applications.
                </p>
                <CodeBlock language="javascript" code={methodMechanicsCode} />
            </div>

            <div className="content-section">
                <h2>Explicit Binding Mechanics</h2>
                <p>
                    Sometimes you need to force a function to use a specific object
                    as its context. This is achieved through <code>call</code>,
                    <code>apply</code>, and <code>bind</code>. These tools are the
                    backbone of library development and decorator patterns.
                </p>
                <CodeBlock language="javascript" code={bindingRulesCode} />
            </div>

            <div className="content-section">
                <h2>The Fluent Interface Pattern</h2>
                <p>
                    By returning <code>this</code> from every method that modifies
                    internal state, you enable <strong>Method Chaining</strong>. This
                    pattern allows for highly readable, declarative APIs (commonly
                    found in Query Builders and Animation libraries).
                </p>
                <CodeBlock language="javascript" code={fluentApiCode} />
            </div>

            <div className="content-section">
                <h2>Lost Context: The Shadow of This</h2>
                <p>
                    A common architectural pitfall is "detaching" a method from its
                    object (e.g., passing it as a callback). Once detached, the
                    implicit link to the object is severed, and <code>this</code>
                    will default to <code>undefined</code> or the global object.
                </p>
                <CodeBlock language="javascript" code={lostContextCode} />
                <ProTip><strong> Arrow Function Rule:</strong> Never use arrow
                    functions for top-level object methods if you need access to
                    <code>this</code>. Arrow functions close over the context where
                    they were <em>created</em>, not where they are <em>called</em>.</ProTip>
            </div>

            <KeyFacts><ul>
                    <li><strong>Implicit Binding:</strong> The object before the dot (<code>obj.method()</code>) becomes <code>this</code>.</li>
                    <li><strong>Explicit Binding:</strong> Use <code>bind()</code> when passing methods as callbacks to preserve identity.</li>
                    <li> <strong>Lexical Scoping:</strong> Remember that arrow functions ignore <code>call/apply</code> binding for <code>this</code>.</li>
                    <li><strong>Memory:</strong> Methods defined on prototypes are more memory-efficient than methods defined in constructors.</li>
                    <li><strong>Fluent APIs:</strong> Return <code>this</code> to allow developers to chain logical operations.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSObjectMethods;



